import React, { useEffect, useState } from "react";
import { Lock, Eye, Skull, VenetianMask, Crown, Key, Ghost, Compass, Anchor, Brain, Flame, Zap, Moon, Telescope, Scale, ScrollText, Feather, Radio, Sparkles, Atom, Snowflake, ShieldAlert, ShieldCheck, Swords, Target, Fingerprint, Spade, Gavel, HelpCircle, Image as ImageIcon, Ship, Users, RotateCw } from "lucide-react";
import { isAdmin, adminHeader } from "../../lib/admin";

const ICONS = {
  eye: Eye, skull: Skull, "venetian-mask": VenetianMask, crown: Crown, lock: Lock, key: Key,
  ghost: Ghost, compass: Compass, anchor: Anchor, brain: Brain, flame: Flame, zap: Zap,
  moon: Moon, telescope: Telescope, scale: Scale, scroll: ScrollText, feather: Feather,
  radio: Radio, sparkles: Sparkles, atom: Atom, snowflake: Snowflake,
  "shield-alert": ShieldAlert, "shield-check": ShieldCheck, swords: Swords, target: Target,
  fingerprint: Fingerprint, spade: Spade, gavel: Gavel, "question-mark": HelpCircle,
  image: ImageIcon, ship: Ship, "users-2": Users,
};

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const AI_RARITIES = new Set(["common", "uncommon", "rare", "mythic", "classified", "divine", "celestial"]);

// Deterministic 32-bit hash so the same card always gets the same SVG layout.
function hash32(s) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// Deterministic PRNG seeded from a 32-bit int.
function mulberry32(seed) {
  return function () {
    seed = (seed + 0x6D2B79F5) | 0;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Procedural brutalist poster used as the card artwork for Common/Uncommon
 * tiers AND as the loading/fallback for Rare+ tiers while their AI image
 * generates. Deterministic — same card ID always renders identical layout.
 *
 * Composition:
 *  - Crosshatch background (rarity-tinted)
 *  - 1-2 large geometric stamp shapes (circle, square, hex)
 *  - Halftone dot field
 *  - Vertical stripe slashes
 *  - Big tinted lucide icon centered, slightly off-axis
 *  - Corner "file index" tick marks
 *  - Pseudo dossier-stamp number bottom-right
 */
function ProceduralPoster({ card, color, height }) {
  const seed = hash32(card?.id || card?.name || "x");
  const rand = mulberry32(seed);
  const Icon = ICONS[card?.icon] || HelpCircle;

  // Random-but-deterministic layout choices
  const shapeType = ["circle", "square", "hex", "triangle"][Math.floor(rand() * 4)];
  const shapeX = 20 + rand() * 60;     // 20-80% horizontal
  const shapeY = 30 + rand() * 40;     // 30-70% vertical
  const shapeSize = 30 + rand() * 25;  // 30-55% width
  const iconOffsetX = (rand() - 0.5) * 18;  // -9 to +9% off center
  const iconOffsetY = (rand() - 0.5) * 14;
  const stampNum = String(Math.floor(rand() * 9000) + 1000);
  const stampSection = "ABCDEFGHJKLMN"[Math.floor(rand() * 13)];
  const halftoneRows = 4 + Math.floor(rand() * 3);
  const stripeAngle = Math.floor(rand() * 4) * 45;  // 0/45/90/135

  // SVG-friendly shape paths
  const renderShape = () => {
    const cx = shapeX, cy = shapeY, s = shapeSize;
    if (shapeType === "circle") {
      return <circle cx={`${cx}%`} cy={`${cy}%`} r={`${s / 2}%`} fill="none" stroke={color} strokeWidth="0.6" opacity="0.55" />;
    }
    if (shapeType === "square") {
      return <rect x={`${cx - s / 2}%`} y={`${cy - s / 2}%`} width={`${s}%`} height={`${s}%`} fill="none" stroke={color} strokeWidth="0.6" opacity="0.55" />;
    }
    if (shapeType === "hex") {
      const r = s / 2;
      const pts = Array.from({ length: 6 }).map((_, i) => {
        const a = (Math.PI / 3) * i + Math.PI / 6;
        return `${cx + Math.cos(a) * r},${cy + Math.sin(a) * r}`;
      }).join(" ");
      return <polygon points={pts} fill="none" stroke={color} strokeWidth="0.6" opacity="0.55" vectorEffect="non-scaling-stroke" />;
    }
    // triangle
    const r = s / 2;
    const pts = [
      `${cx},${cy - r}`,
      `${cx + r * 0.87},${cy + r * 0.5}`,
      `${cx - r * 0.87},${cy + r * 0.5}`,
    ].join(" ");
    return <polygon points={pts} fill="none" stroke={color} strokeWidth="0.6" opacity="0.55" vectorEffect="non-scaling-stroke" />;
  };

  // Halftone dot field — small offset dots in a grid, smaller toward the edge
  const dots = [];
  const cols = 12;
  for (let r = 0; r < halftoneRows; r++) {
    for (let c = 0; c < cols; c++) {
      const dx = (c + 0.5) / cols * 100;
      const dy = 8 + (r / (halftoneRows - 1 || 1)) * 18;
      const sz = 0.4 + (1 - Math.abs(c - cols / 2) / cols) * 0.6;
      dots.push(
        <circle key={`d${r}-${c}`} cx={`${dx}%`} cy={`${dy}%`} r={sz}
                fill={color} opacity="0.35" />
      );
    }
  }

  return (
    <div className="relative w-full overflow-hidden" style={{ height }}>
      {/* Base background gradient — radial color burst */}
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(circle at ${50 + iconOffsetX}% ${50 + iconOffsetY}%, ${color}40 0%, transparent 60%), #0a0a0a`,
        }}
      />

      {/* SVG art layer */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        {/* Crosshatch lines */}
        <defs>
          <pattern id={`hatch-${seed}`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform={`rotate(${stripeAngle})`}>
            <line x1="0" y1="0" x2="0" y2="6" stroke={color} strokeWidth="0.3" opacity="0.18" />
          </pattern>
        </defs>
        <rect width="100" height="100" fill={`url(#hatch-${seed})`} />

        {/* Big geometric stamp */}
        {renderShape()}

        {/* Halftone dots top */}
        {dots}

        {/* Bottom diagonal slash */}
        <line x1="0" y1="92" x2={50 + rand() * 30} y2="100" stroke={color} strokeWidth="0.5" opacity="0.5" />

        {/* Corner tick marks */}
        <g stroke={color} strokeWidth="0.4" opacity="0.7" fill="none">
          <path d="M 2 2 L 8 2 M 2 2 L 2 8" />
          <path d="M 98 2 L 92 2 M 98 2 L 98 8" />
          <path d="M 2 98 L 8 98 M 2 98 L 2 92" />
          <path d="M 98 98 L 92 98 M 98 98 L 98 92" />
        </g>

        {/* Dossier stamp number bottom-left, monospace */}
        <text
          x="3.5" y="95"
          fill={color}
          opacity="0.6"
          fontFamily="ui-monospace, SF Mono, Menlo, monospace"
          fontSize="3"
          letterSpacing="0.4"
        >
          {`§${stampSection}-${stampNum}`}
        </text>
      </svg>

      {/* Grain overlay */}
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85'/%3E%3C/filter%3E%3Crect width='80' height='80' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")",
      }} />

      {/* Centered icon — the focal point */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          transform: `translate(${iconOffsetX * 0.4}%, ${iconOffsetY * 0.4}%)`,
        }}
      >
        <Icon size={Math.round(height * 0.5)} color={color} strokeWidth={1.5} />
      </div>
    </div>
  );
}

/**
 * CardArtwork — main entry point. Decides between AI-generated artwork
 * (fetched from /api/vault-cards/{id}/artwork.png) and the procedural SVG
 * poster. Rare+ tiers get AI art; everything else stays on the SVG.
 *
 * Props:
 *   card    — catalog object
 *   color   — rarity hex color
 *   height  — pixel height
 */
export default function CardArtwork({ card, color, height }) {
  const rarity = card?.rarity || "common";
  const wantAI = AI_RARITIES.has(rarity) && card?.id;
  const [aiUrl, setAiUrl] = useState(null);
  // 'idle' | 'generating' | 'ready' | 'failed' — 'idle' for non-AI cards
  const [aiStatus, setAiStatus] = useState(wantAI ? "generating" : "idle");

  useEffect(() => {
    if (!wantAI) return;
    let cancel = false;
    const url = `${API_BASE}/api/vault-cards/${card.id}/artwork.png`;
    const prewarmUrl = `${API_BASE}/api/vault-cards/${card.id}/artwork/prewarm`;

    // Fire prewarm (idempotent) so generation kicks off in the background
    // regardless of whether this card was pulled before AI art shipped.
    fetch(prewarmUrl, { method: "POST", credentials: "omit" }).catch(() => {});

    // Fully decodes the image off-screen before flipping aiStatus to "ready",
    // so the swap from SVG → AI art is a single seamless paint with no flash
    // of broken image / network delay.
    const preload = (src) => new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        if (typeof img.decode === "function") {
          img.decode().then(() => resolve(src)).catch(() => resolve(src));
        } else {
          resolve(src);
        }
      };
      img.onerror = reject;
      img.src = src;
    });

    const probe = async () => {
      try {
        const res = await fetch(url, { credentials: "omit", cache: "default" });
        if (cancel) return;
        if (res.status === 200) {
          // Wait for the image to be fully decoded before showing it. This
          // keeps the GEN badge visible while bytes download.
          setAiStatus("generating");
          try {
            await preload(url);
            if (cancel) return;
            setAiUrl(url);
            setAiStatus("ready");
          } catch (_e) {
            if (!cancel) setAiStatus("failed");
          }
          return;
        }
        if (res.status === 202) {
          setAiStatus("generating");
          setTimeout(probe, 4500);
          return;
        }
        // 404 / 422 — give up permanently
        setAiStatus("failed");
      } catch (_e) {
        if (!cancel) setAiStatus("failed");
      }
    };
    probe();
    return () => { cancel = true; };
  }, [wantAI, card?.id]);

  // While AI art is loading (or if it failed), show the procedural poster.
  // If AI art is available, layer it over the same poster background so the
  // edge halftones / corner ticks still tie it to the brutalist look.
  const onRetry = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const res = await fetch(`${API_BASE}/api/vault-cards/${card.id}/artwork/retry`, {
        method: "POST",
        headers: adminHeader(),
        credentials: "omit",
      });
      if (!res.ok) return;
      // Re-trigger the probe by toggling state — pull a fresh load.
      setAiUrl(null);
      setAiStatus("generating");
      // Kick the probe back off by re-running the effect via a dummy state.
      // Simplest: just re-issue a probe loop inline.
      const url = `${API_BASE}/api/vault-cards/${card.id}/artwork.png`;
      const reprobe = async () => {
        try {
          const r = await fetch(url, { credentials: "omit", cache: "no-store" });
          if (r.status === 200) {
            setAiUrl(url + `?ts=${Date.now()}`);
            setAiStatus("ready");
            return;
          }
          if (r.status === 202) {
            setTimeout(reprobe, 4500);
            return;
          }
          setAiStatus("failed");
        } catch {
          setAiStatus("failed");
        }
      };
      reprobe();
    } catch {
      /* silent */
    }
  };

  return (
    <div className="relative w-full" style={{ height }}>
      <ProceduralPoster card={card} color={color} height={height} />
      {aiUrl && (
        <img
          src={aiUrl}
          alt=""
          draggable={false}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
          style={{ opacity: aiStatus === "ready" ? 1 : 0 }}
          onLoad={() => setAiStatus("ready")}
        />
      )}
      {aiStatus === "generating" && (
        <div
          className="absolute bottom-1 left-1 z-[3] flex items-center gap-1 px-1.5 py-0.5 bg-[#050505]/85 border font-mono text-[8px] tracking-[0.2em]"
          style={{ borderColor: `${color}88`, color }}
          title="Unique archival artwork is being generated for this card"
        >
          <span
            className="inline-block w-1.5 h-1.5 rounded-full animate-pulse"
            style={{ backgroundColor: color }}
          />
          GEN…
        </div>
      )}
      {aiStatus === "failed" && wantAI && (
        <div
          className="absolute bottom-1 left-1 z-[3] flex items-center gap-1 px-1.5 py-0.5 bg-[#050505]/85 border font-mono text-[8px] tracking-[0.2em] text-[#ff3b30]"
          style={{ borderColor: "#ff3b3088" }}
          title="AI artwork generation failed (likely Nano Banana safety filter). Showing brutalist fallback."
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#ff3b30]" />
          NO ART
        </div>
      )}
      {aiStatus === "failed" && wantAI && isAdmin() && (
        <button
          type="button"
          onClick={onRetry}
          data-testid={`card-art-retry-${card.id}`}
          title="Admin: retry artwork generation for this card"
          className="absolute bottom-1 right-1 z-[4] flex items-center gap-1 px-1.5 py-0.5 bg-[#050505]/85 border border-white/30 font-mono text-[8px] tracking-[0.2em] text-zinc-300 hover:text-white hover:border-white/60"
        >
          <RotateCw size={9} /> RETRY
        </button>
      )}
      {aiStatus === "ready" && wantAI && isAdmin() && (
        <button
          type="button"
          onClick={onRetry}
          data-testid={`card-art-regen-${card.id}`}
          title="Admin: regenerate this card's AI artwork (burns a fresh Nano Banana call)"
          className="absolute bottom-1 right-1 z-[4] flex items-center gap-1 px-1.5 py-0.5 bg-[#050505]/85 border border-white/20 font-mono text-[8px] tracking-[0.2em] text-zinc-400 hover:text-white hover:border-white/60 opacity-0 group-hover/card:opacity-100 focus:opacity-100 transition-opacity"
        >
          <RotateCw size={9} /> REGEN
        </button>
      )}
    </div>
  );
}
